<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { Message, Modal } from '@arco-design/web-vue';
import { api } from './api.js';
import PlatformLogo from './components/PlatformLogo.vue';
import { parseImportSheet } from './import-spreadsheet.js';
import {
  buildViewTrend,
  comparableViewSnapshots,
  compactNumber,
  formatDate,
  formatDateOnly,
  formatNumber,
  platformNames,
  statusMeta,
  videoDisplayTitle,
} from './format.js';

const PAGE_STORAGE_KEY = 'ouyi-video-current-page';
const importTemplateUrl = `${import.meta.env.BASE_URL}video-import-template.xlsx`;
const navigation = Object.freeze([
  { key: 'home', icon: '⌂', label: '数据首页' },
  { key: 'videos', icon: '▣', label: '视频管理' },
  { key: 'products', icon: '◇', label: '商品管理' },
  { key: 'creators', icon: '◉', label: '博主管理' },
  { key: 'tasks', icon: '◷', label: '采集任务' },
  { key: 'logs', icon: '≡', label: '操作日志' },
  { key: 'settings', icon: '⚙', label: '系统设置' },
]);

function initialPage() {
  try {
    const savedPage = window.localStorage.getItem(PAGE_STORAGE_KEY);
    return navigation.some((item) => item.key === savedPage) ? savedPage : 'home';
  } catch {
    return 'home';
  }
}

const authLoading = ref(true);
const feishuLoginEnabled = ref(false);
const feishuRedirecting = ref(null);
let feishuRedirectTimer;
let feishuRedirectWatchdog;
const currentUser = ref(null);
const loginLoading = ref(false);
const loginError = ref('');
const loginForm = reactive({ username: '', password: '' });
const changePasswordVisible = ref(false);
const changePasswordLoading = ref(false);
const changePasswordForm = reactive({ currentPassword: '', newPassword: '', confirmPassword: '' });
const page = ref(initialPage());
const collectorMode = ref('mock');
const loading = ref(false);
const dashboardLoading = ref(false);
const reportMode = ref('published');
const beijingToday = new Date(Date.now() + 8 * 60 * 60 * 1000);
const reportFrom = ref(new Date(beijingToday.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
const reportTo = ref(beijingToday.toISOString().slice(0, 10));
const reportLoading = ref(false);
const reportData = ref(null);
let reportRequestId = 0;
const dashboard = ref({
  platforms: {},
  summary: { videos: 0, covered: 0, coverageRate: 0, creators: 0, snapshots: 0, shares: '0', saves: '0', reposts: '0', lastCollectedAt: null },
  trend: { months: [], series: {} },
});
const trendMetric = ref('views');
const videos = ref([]);
const stats = ref({ total: 0, success: 0, partial: 0, failed: 0, pending: 0, totalViews: '0', viewsCovered: 0 });
const pagination = ref({ page: 1, pageSize: 20, total: 0 });
const filters = reactive({ search: '', platform: 'all', productId: 'all', status: 'all', sort: 'created_desc', archived: false, dateType: 'published', dateFrom: '', dateTo: '', page: 1, pageSize: 20 });
const selectedKeys = ref([]);
const videoTab = ref('all');
const brokenThumbnails = reactive(new Set());
const brokenAvatars = reactive(new Set());
const importVisible = ref(false);
const importMode = ref('paste');
const importLinks = ref('');
const importRows = ref([]);
const importFileName = ref('');
const importProductId = ref(null);
const importPreview = ref(null);
const importResult = ref(null);
const importing = ref(false);
const importStage = ref('input');
const importTask = ref(null);
const detailVisible = ref(false);
const detail = ref(null);
const snapshots = ref([]);
const detailLoading = ref(false);
const noteDraft = ref('');
const tasks = ref([]);
const taskPagination = ref({ page: 1, pageSize: 20, total: 0 });
const taskVisible = ref(false);
const selectedTask = ref(null);
const logs = ref([]);
const logPagination = ref({ page: 1, pageSize: 30, total: 0 });
const creators = ref([]);
const creatorSavingIds = reactive(new Set());
const creatorImportVisible = ref(false);
const creatorProfileUrl = ref('');
const creatorImporting = ref(false);
const creatorLinkDraft = ref(null);
const creatorLinkSaving = ref(false);
const productData = ref({ items: [], summary: { products: 0, videos: 0, assigned: 0, unassigned: 0 } });
const productVisible = ref(false);
const productName = ref('');
const productEditingId = ref(null);
const productSaving = ref(false);
const productLinkDraft = ref(null);
const productLinkSaving = ref(false);
const settings = reactive({
  frequencyType: 'manual',
  timeOfDay: '09:00',
  weekday: 1,
  monthday: 1,
  intervalDays: 7,
  isPaused: true,
  monthlyBudgetCny: 250,
  nextRunAt: null,
  scheduler: { workerActive: false, tickRunning: false, lastTickAt: null, lastTriggeredAt: null, lastTaskId: null, lastError: null },
});
const settingsLoading = ref(false);
const feishu = reactive({
  appId: '', appSecret: '', spreadsheetUrl: '', hasSecret: false, configured: false,
  lastSyncAt: null, lastSyncStatus: null, lastSyncMessage: '',
});
const feishuLoading = ref(false);
const feishuSyncing = ref(false);
const currentTime = ref(new Date());
let pollTimer;
let clockTimer;
let importPollTimer;

const pageTitle = computed(() => navigation.find((item) => item.key === page.value)?.label ?? '工作台');
const isLive = computed(() => collectorMode.value === 'live');
const activeTasks = computed(() => tasks.value.some((task) => ['pending', 'running'].includes(task.status)));
const activeScheduledTask = computed(() => tasks.value.find((task) => task.triggerType === 'scheduled' && ['pending', 'running'].includes(task.status)));
const latestScheduledTask = computed(() => tasks.value.find((task) => task.triggerType === 'scheduled'));
const scheduleState = computed(() => {
  if (!settings.scheduler?.workerActive) return { label: '调度服务异常', tone: 'error', detail: publicErrorMessage(settings.scheduler?.lastError) || '后台调度器未运行' };
  if (settings.isPaused || settings.frequencyType === 'manual') return { label: '自动采集已暂停', tone: 'paused', detail: '当前不会自动创建采集任务' };
  if (activeScheduledTask.value) return { label: '自动采集中', tone: 'running', detail: `任务 #${activeScheduledTask.value.id} 正在处理 ${activeScheduledTask.value.total} 条视频` };
  return { label: '自动采集运行中', tone: 'success', detail: settings.nextRunAt ? `下次执行：${formatDate(settings.nextRunAt)}` : '等待下次计划' };
});
const platformColors = Object.freeze({ tiktok: '#353b48', instagram: '#c16cae', facebook: '#5d8fd3', youtube: '#df6a6a' });
const platformList = computed(() => Object.keys(platformNames).map((platform) => dashboard.value.platforms[platform] ?? {
  platform, videos: 0, views: '0', engagement: '0', comments: '0', success: 0, attention: 0,
}));
const totalEngagement = computed(() => platformList.value.reduce((sum, item) => sum + Number(item.engagement), 0));
const totalComments = computed(() => platformList.value.reduce((sum, item) => sum + Number(item.comments), 0));
const detailPlatformCreators = computed(() => creators.value.filter((creator) => creator.platform === detail.value?.platform));
const products = computed(() => productData.value.items);
const productTotals = computed(() => products.value.reduce((total, product) => ({
  views: total.views + Number(product.totalViews),
  engagement: total.engagement + Number(product.totalEngagement),
  creators: total.creators + Number(product.creatorCount),
}), { views: 0, engagement: 0, creators: 0 }));
const detailTrendSnapshots = computed(() => comparableViewSnapshots(snapshots.value, detail.value?.platform));
const detailTrendSourceChanged = computed(() => detailTrendSnapshots.value.length < snapshots.value.length);
const detailViewTrend = computed(() => buildViewTrend(detailTrendSnapshots.value));
const detailViewChange = computed(() => {
  const points = detailViewTrend.value.points;
  return points.length >= 2 ? points.at(-1).views - points[0].views : null;
});
const importCollectionActive = computed(() => ['saving', 'collecting'].includes(importStage.value));
const importCollectionDone = computed(() => ['success', 'partial', 'failed'].includes(importTask.value?.status));
const importInput = computed(() => importMode.value === 'file' ? importRows.value : importLinks.value);
watch(importProductId, () => {
  if (importMode.value === 'file' && importRows.value.length && importStage.value === 'input') previewLinks();
});
const trendMetricLabels = Object.freeze({ videos: '采集视频', views: '播放量', engagement: '点赞/反应', comments: '评论数' });
const chartMax = computed(() => Math.max(1, ...platformList.value.flatMap((platform) => (
  dashboard.value.trend.series?.[platform.platform] ?? []
).map((item) => Number(item[trendMetric.value]) || 0))));
const chartLines = computed(() => platformList.value.map((platform) => {
  const data = dashboard.value.trend.series?.[platform.platform] ?? [];
  const width = 820;
  const points = data.map((item, index) => {
    const x = 64 + (data.length <= 1 ? 0 : index * width / (data.length - 1));
    const y = 205 - (Number(item[trendMetric.value]) || 0) / chartMax.value * 155;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return {
    platform: platform.platform,
    color: platformColors[platform.platform],
    points,
    latest: data.at(-1)?.[trendMetric.value] ?? 0,
  };
}));
const chartGuides = computed(() => [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({
  y: 205 - ratio * 155,
  label: compactNumber(Math.round(chartMax.value * ratio)),
})));
const beijingTime = computed(() => new Intl.DateTimeFormat('zh-CN', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}).format(currentTime.value));
const videoColumns = [
  { title: '视频内容', slotName: 'content', minWidth: 360 },
  { title: '商品', slotName: 'product', width: 130 },
  { title: '作者', slotName: 'author', width: 165 },
  { title: '播放量', slotName: 'views', width: 110 },
  { title: '点赞/反应', slotName: 'engagement', width: 115 },
  { title: '评论数', slotName: 'comments', width: 90 },
  { title: '采集状态', slotName: 'status', width: 130 },
  { title: '操作', slotName: 'actions', width: 90 },
];
const productColumns = [
  { title: '商品名称', slotName: 'name', width: 135 },
  { title: '红人', slotName: 'creators', width: 68 },
  { title: '平台视频', slotName: 'platforms', minWidth: 210 },
  { title: '播放量', slotName: 'views', width: 88 },
  { title: '互动数', slotName: 'engagement', width: 88 },
  { title: '互动率', slotName: 'rate', width: 76 },
  { title: '采集状态', slotName: 'status', width: 112 },
  { title: '最近采集', slotName: 'collectedAt', width: 132 },
  { title: '操作', slotName: 'actions', width: 116 },
];
const snapshotColumns = [
  { title: '采集时间', slotName: 'time', width: 180 },
  { title: '播放', slotName: 'views' },
  { title: '点赞/反应', slotName: 'engagement' },
  { title: '评论', slotName: 'comments' },
  { title: '结果', slotName: 'status' },
];
const taskColumns = [
  { title: '任务', dataIndex: 'id', width: 100 },
  { title: '创建时间', slotName: 'createdAt', width: 180 },
  { title: '触发方式', slotName: 'trigger', width: 145 },
  { title: '采集数量', slotName: 'count', width: 120 },
  { title: '执行时间', slotName: 'execution', width: 210 },
  { title: '处理结果', slotName: 'result', minWidth: 230 },
  { title: '状态', slotName: 'status', width: 115 },
  { title: '', slotName: 'actions', width: 80 },
];
const logColumns = [
  { title: '类型', slotName: 'type', width: 110 },
  { title: '记录时间', slotName: 'occurredAt', width: 180 },
  { title: '方式 / 范围', slotName: 'scope', width: 150 },
  { title: '操作人', slotName: 'operator', width: 215 },
  { title: '数量', slotName: 'count', width: 100 },
  { title: '执行时间', slotName: 'execution', width: 245 },
  { title: '执行结果', slotName: 'result', minWidth: 230 },
  { title: '状态', slotName: 'status', width: 110 },
  { title: '', slotName: 'actions', width: 75 },
];

async function loadApplication() {
  await Promise.all([refreshVideoData(), loadDashboard(), loadTasks(), loadProducts(), loadSettings(), loadSystemStatus()]);
  if (page.value === 'creators') await loadCreators();
  if (page.value === 'logs') await loadLogs(1);
  window.clearInterval(pollTimer);
  pollTimer = window.setInterval(loadTasks, 4_000);
}

async function login() {
  if (!loginForm.username.trim() || !loginForm.password) {
    loginError.value = '请输入用户名和密码';
    return;
  }
  loginLoading.value = true;
  loginError.value = '';
  try {
    currentUser.value = await api.login(loginForm);
    loginForm.password = '';
    await loadApplication();
  } catch (error) {
    loginError.value = error.message;
  } finally {
    loginLoading.value = false;
  }
}

function openFeishuLogin() {
  if (feishuRedirecting.value) return;
  feishuRedirecting.value = 'login';
  // 留出一帧让加载提示先显示，再跳转到飞书授权页。
  feishuRedirectTimer = window.setTimeout(() => {
    try {
      window.location.assign('/api/auth/feishu/start');
    } catch {
      resetFeishuRedirect();
      Message.error('无法打开飞书授权页面，请稍后重试');
    }
  }, 100);
  feishuRedirectWatchdog = window.setTimeout(() => {
    if (!feishuRedirecting.value) return;
    resetFeishuRedirect();
    Message.error('打开飞书授权页面超时，请检查网络后重试');
  }, 30_000);
}

function resetFeishuRedirect() {
  window.clearTimeout(feishuRedirectTimer);
  window.clearTimeout(feishuRedirectWatchdog);
  feishuRedirecting.value = null;
}

function showFeishuResult() {
  const url = new URL(window.location.href);
  const result = url.searchParams.get('feishu');
  if (!result) return;
  url.searchParams.delete('feishu');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  if (result === 'login') Message.success('已通过飞书登录');
  else if (result === 'cancelled') loginError.value = '飞书授权已取消';
  else if (result === 'failed') loginError.value = '飞书身份验证失败，请稍后重试';
}

async function logout() {
  try {
    await api.logout();
  } finally {
    window.clearInterval(pollTimer);
    currentUser.value = null;
    videos.value = [];
    tasks.value = [];
    loginForm.password = '';
  }
}

async function submitPasswordChange() {
  if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
    Message.error('两次输入的新密码不一致');
    return;
  }
  changePasswordLoading.value = true;
  try {
    currentUser.value = await api.changePassword({
      currentPassword: changePasswordForm.currentPassword,
      newPassword: changePasswordForm.newPassword,
    });
    Object.assign(changePasswordForm, { currentPassword: '', newPassword: '', confirmPassword: '' });
    changePasswordVisible.value = false;
    Message.success('密码修改成功');
  } catch (error) {
    Message.error(error.message);
  } finally {
    changePasswordLoading.value = false;
  }
}

function changeVideoTab(tab) {
  videoTab.value = tab;
  filters.archived = tab === 'archived';
  filters.status = tab === 'failed' ? 'temporarily_failed' : 'all';
  applyFilters();
}

function markThumbnailBroken(id) {
  brokenThumbnails.add(String(id));
}

function markAvatarBroken(id) {
  brokenAvatars.add(String(id));
}

async function loadDashboard() {
  dashboardLoading.value = true;
  try {
    const [overview] = await Promise.all([api.dashboard(), loadReport()]);
    dashboard.value = overview;
  } catch (error) {
    Message.error(error.message);
  } finally {
    dashboardLoading.value = false;
  }
}

async function loadReport() {
  if (!reportFrom.value || !reportTo.value || reportFrom.value > reportTo.value) {
    reportRequestId += 1;
    reportData.value = null;
    reportLoading.value = false;
    Message.warning('请选择有效的起止日期');
    return;
  }
  const requestId = ++reportRequestId;
  const mode = reportMode.value;
  reportLoading.value = true;
  reportData.value = null;
  try {
    const range = { from: reportFrom.value, to: reportTo.value };
    const result = mode === 'published'
      ? await api.publishedReport(range)
      : await api.collectionReport(range);
    if (requestId === reportRequestId) reportData.value = result;
  } catch (error) {
    if (requestId === reportRequestId) Message.error(error.message);
  } finally {
    if (requestId === reportRequestId) reportLoading.value = false;
  }
}

function changeReportMode(mode) {
  reportMode.value = mode;
  reportData.value = null;
  loadReport();
}

function showPublishedVideos() {
  videoTab.value = 'all';
  filters.archived = false;
  filters.status = 'all';
  filters.platform = 'all';
  filters.productId = 'all';
  filters.search = '';
  filters.sort = 'published_desc';
  filters.dateType = 'published';
  filters.dateFrom = reportData.value.from;
  filters.dateTo = reportData.value.to;
  filters.page = 1;
  navigate('videos');
}

function exportVideos() {
  if (!videos.value.length) return Message.warning('当前没有可导出的数据');
  const rows = [
    ['商品', '平台', '标题', '作者', '视频ID', '播放量', '点赞/反应', '评论数', '采集状态', '最后成功', '链接'],
    ...videos.value.map((video) => [
      video.product?.name ?? '', platformNames[video.platform], videoDisplayTitle(video), video.author?.displayName ?? '', video.platformVideoId,
      video.metrics.views ?? '', video.platform === 'facebook' ? video.metrics.reactions ?? '' : video.metrics.likes ?? '',
      video.metrics.comments ?? '', statusMeta(video.status).label, video.lastSuccessAt ?? '', video.canonicalUrl,
    ]),
  ];
  const escapeCell = (value) => `"${String(value).replaceAll('"', '""')}"`;
  const csv = `\uFEFF${rows.map((row) => row.map(escapeCell).join(',')).join('\r\n')}`;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  link.download = `视频数据-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

async function loadVideos() {
  loading.value = true;
  try {
    const data = await api.videos(filters);
    videos.value = data.items;
    pagination.value = data.pagination;
    selectedKeys.value = [];
  } catch (error) {
    Message.error(error.message);
  } finally {
    loading.value = false;
  }
}

async function loadStats() {
  try {
    stats.value = await api.videoStats();
  } catch (error) {
    Message.error(error.message);
  }
}

async function loadSystemStatus() {
  try {
    const health = await api.health();
    collectorMode.value = health.collectorMode ?? 'mock';
  } catch (error) {
    Message.error(error.message);
  }
}

async function refreshVideoData() {
  await Promise.all([loadVideos(), loadStats()]);
}

function changePage(nextPage) {
  filters.page = nextPage;
  loadVideos();
}

function applyFilters() {
  if ((filters.dateFrom || filters.dateTo) && (!filters.dateFrom || !filters.dateTo || filters.dateFrom > filters.dateTo)) {
    Message.warning('请选择有效的时间范围');
    return;
  }
  filters.page = 1;
  loadVideos();
}

function clearDateRange() {
  filters.dateFrom = '';
  filters.dateTo = '';
  applyFilters();
}

async function previewLinks() {
  if (importMode.value === 'file' ? !importRows.value.length : !importLinks.value.trim()) {
    return Message.warning(importMode.value === 'file' ? '请先选择表格' : '请先粘贴视频链接');
  }
  importing.value = true;
  try {
    importPreview.value = await api.previewImport(importInput.value, importProductId.value);
  } catch (error) {
    Message.error(error.message);
  } finally {
    importing.value = false;
  }
}

async function selectImportFile(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  importPreview.value = null;
  importRows.value = [];
  importFileName.value = '';
  if (!file.name.toLowerCase().endsWith('.xlsx') || file.size > 2 * 1024 * 1024) {
    return Message.error('请选择不超过 2 MB 的 .xlsx 表格');
  }
  importing.value = true;
  try {
    const { readSheet } = await import('read-excel-file/browser');
    importRows.value = parseImportSheet(await readSheet(file));
    importFileName.value = file.name;
  } catch (error) {
    Message.error(`读取表格失败：${error.message}`);
  } finally {
    importing.value = false;
  }
  if (importRows.value.length) await previewLinks();
}

async function confirmLinks() {
  importing.value = true;
  importStage.value = 'saving';
  let result;
  try {
    result = await api.confirmImport(importInput.value, importProductId.value);
    importResult.value = result;
  } catch (error) {
    importStage.value = 'input';
    Message.error(error.message);
    return;
  } finally {
    importing.value = false;
  }
  try {
    if (result.addedVideoIds.length) {
      importTask.value = await api.refreshVideos(result.addedVideoIds, 'manual_batch');
      importStage.value = 'collecting';
      scheduleImportTaskPoll();
    } else {
      importStage.value = 'complete';
    }
    await Promise.all([refreshVideoData(), loadDashboard(), loadTasks(), loadProducts(), loadCreators()]);
  } catch (error) {
    importStage.value = 'complete';
    Message.warning(`视频已入库，但采集任务未启动：${error.message}。可在视频管理中重新采集。`);
  }
}

function openImportDialog(mode = 'paste') {
  importMode.value = mode;
  resetImportDialog();
  importVisible.value = true;
}

function resetImportDialog() {
  window.clearTimeout(importPollTimer);
  importLinks.value = '';
  importRows.value = [];
  importFileName.value = '';
  importProductId.value = null;
  importPreview.value = null;
  importResult.value = null;
  importTask.value = null;
  importStage.value = 'input';
}

function closeImportDialog() {
  if (importCollectionActive.value) return;
  importVisible.value = false;
  resetImportDialog();
}

function collectionProgress(status) {
  return { pending: 12, running: 62, success: 100, partial: 100, failed: 100 }[status] ?? 0;
}

function collectionStatusText(item) {
  if (item.status === 'pending') return '等待采集';
  if (item.status === 'running') return '正在获取平台数据并保存到本服务器';
  if (item.status === 'success') return '采集完成';
  if (item.status === 'partial') return publicErrorMessage(item.errorMessage) || '采集完成，部分指标暂未提供';
  if (item.status === 'failed') return publicErrorMessage(item.errorMessage) || '采集失败，请稍后重试';
  return statusMeta(item.status).label;
}

function publicErrorMessage(value) {
  return String(value ?? '').replace(/Apify(?:\s+Actor)?(?:\s+[A-Za-z0-9_./-]+)?/gi, '真实数据采集服务');
}

function scheduleImportTaskPoll(delay = 700) {
  window.clearTimeout(importPollTimer);
  importPollTimer = window.setTimeout(pollImportTask, delay);
}

async function pollImportTask() {
  if (!importTask.value?.id || !importVisible.value) return;
  try {
    importTask.value = await api.task(importTask.value.id);
    if (!importCollectionDone.value) {
      scheduleImportTaskPoll(1_000);
      return;
    }
    importStage.value = 'complete';
    await Promise.all([refreshVideoData(), loadDashboard(), loadTasks(), loadProducts(), loadCreators()]);
  } catch (error) {
    Message.error(`读取采集进度失败：${error.message}`);
    scheduleImportTaskPoll(2_000);
  }
}

async function createCreator() {
  if (!creatorProfileUrl.value.trim()) return Message.warning('请输入作者主页链接');
  creatorImporting.value = true;
  try {
    const result = await api.createCreator(creatorProfileUrl.value.trim());
    Message.success(result.restored ? '博主已恢复，采集仍处于暂停状态' : result.created ? '博主已创建并完成资料采集' : '该博主已存在，已直接复用');
    creatorImportVisible.value = false;
    creatorProfileUrl.value = '';
    await Promise.all([loadCreators(), loadDashboard()]);
  } catch (error) {
    Message.error(error.message);
  } finally {
    creatorImporting.value = false;
  }
}

async function openDetail(video) {
  detailVisible.value = true;
  detailLoading.value = true;
  try {
    const [videoData, snapshotData, creatorData] = await Promise.all([
      api.video(video.id),
      api.snapshots(video.id, { pageSize: 50 }),
      api.creators(),
    ]);
    detail.value = videoData;
    snapshots.value = snapshotData.items;
    creators.value = creatorData;
    creatorLinkDraft.value = videoData.author?.id ?? null;
    productLinkDraft.value = videoData.product?.id ?? null;
    noteDraft.value = videoData.note;
  } catch (error) {
    Message.error(error.message);
  } finally {
    detailLoading.value = false;
  }
}

async function saveProductLink() {
  if (!detail.value) return;
  productLinkSaving.value = true;
  try {
    detail.value = await api.setVideoProduct(detail.value.id, productLinkDraft.value || null);
    productLinkDraft.value = detail.value.product?.id ?? null;
    Message.success(detail.value.product ? '商品关联已保存' : '已解除商品关联');
    await Promise.all([loadVideos(), loadProducts(), loadCreators(), loadDashboard()]);
  } catch (error) {
    Message.error(error.message);
  } finally {
    productLinkSaving.value = false;
  }
}

async function saveCreatorLink(mode = 'manual') {
  if (!detail.value) return;
  creatorLinkSaving.value = true;
  try {
    detail.value = await api.setVideoCreator(detail.value.id, creatorLinkDraft.value || null, mode);
    creatorLinkDraft.value = detail.value.author?.id ?? null;
    Message.success(mode === 'auto'
      ? '已恢复自动关联，下次采集会按作者身份重新匹配'
      : detail.value.author ? '视频已人工关联到所选博主' : '已人工解除视频与博主的关联');
    await Promise.all([loadVideos(), loadCreators(), loadDashboard()]);
  } catch (error) {
    Message.error(error.message);
  } finally {
    creatorLinkSaving.value = false;
  }
}

async function saveNote() {
  try {
    detail.value = await api.updateVideo(detail.value.id, { note: noteDraft.value });
    Message.success('备注已保存');
    await loadVideos();
  } catch (error) {
    Message.error(error.message);
  }
}

async function refreshOne(id) {
  try {
    await api.refreshVideo(id);
    Message.success('已创建单条采集任务');
    await loadTasks();
  } catch (error) {
    Message.error(error.message);
  }
}

async function refreshSelected() {
  if (!selectedKeys.value.length) return Message.warning('请先选择视频');
  try {
    await api.refreshVideos(selectedKeys.value, 'manual_batch');
    Message.success(`已创建 ${selectedKeys.value.length} 条视频的采集任务`);
    selectedKeys.value = [];
    await loadTasks();
  } catch (error) {
    Message.error(error.message);
  }
}

async function refreshAll() {
  try {
    await api.refreshVideos(undefined, 'manual_all');
    Message.success(`已创建全量${isLive.value ? '真实' : ' Mock'}采集任务`);
    await loadTasks();
  } catch (error) {
    Message.error(error.message);
  }
}

function confirmArchive(video) {
  Modal.confirm({
    title: video.archived ? '恢复视频' : '归档视频',
    content: video.archived ? '恢复后将重新加入统一采集计划。' : '归档会停止自动采集，但历史数据仍保留。',
    onOk: async () => {
      if (video.archived) await api.restoreVideo(video.id);
      else await api.archiveVideo(video.id);
      Message.success(video.archived ? '已恢复' : '已归档');
      await Promise.all([refreshVideoData(), loadDashboard()]);
    },
  });
}

async function loadTasks() {
  try {
    const data = await api.tasks({ pageSize: 50 });
    const hadActive = activeTasks.value;
    tasks.value = data.items;
    taskPagination.value = data.pagination;
    if (hadActive && !activeTasks.value) await Promise.all([refreshVideoData(), loadDashboard()]);
  } catch (error) {
    if (page.value === 'tasks') Message.error(error.message);
  }
}

async function openTask(task) {
  try {
    selectedTask.value = await api.task(task.id);
    taskVisible.value = true;
  } catch (error) {
    Message.error(error.message);
  }
}

async function loadLogs(pageNumber = logPagination.value.page) {
  try {
    const data = await api.logs({ page: pageNumber, pageSize: logPagination.value.pageSize });
    logs.value = data.items;
    logPagination.value = data.pagination;
  } catch (error) {
    if (page.value === 'logs') Message.error(error.message);
  }
}

function changeLogPage(nextPage) {
  loadLogs(nextPage);
}

function triggerLabel(triggerType) {
  return {
    manual_single: '单条手动',
    manual_batch: '批量手动',
    manual_all: '手动全量',
    scheduled: '自动计划',
  }[triggerType] || triggerType || '链接录入';
}

function operationLabel(record) {
  const path = record.requestPath || '';
  if (path === '/api/auth/change-password') return '修改系统密码';
  if (path === '/api/auth/feishu/callback') return '飞书成员登录';
  if (path === '/api/auth/login') return '系统账号登录';
  if (path === '/api/videos/import/confirm') return '确认导入视频';
  if (/^\/api\/videos\/\d+\/refresh$/.test(path)) return '单条采集';
  if (path === '/api/videos/refresh') return '批量采集';
  if (path === '/api/products') return '添加商品';
  if (/^\/api\/products\/\d+$/.test(path)) return '修改商品';
  if (path === '/api/creators') return '添加博主';
  if (/^\/api\/creators\/\d+\/collection$/.test(path)) return '切换博主采集';
  if (/^\/api\/creators\/\d+$/.test(path)) return '删除博主';
  if (path === '/api/settings/collection') return '修改采集计划';
  if (path.startsWith('/api/settings/collection/')) return '切换采集计划状态';
  if (path === '/api/integrations/feishu') return '修改飞书同步配置';
  if (path === '/api/integrations/feishu/sync') return '同步飞书表格';
  if (/^\/api\/videos\/\d+\/product$/.test(path)) return '关联视频商品';
  if (/^\/api\/videos\/\d+\/creator$/.test(path)) return '关联视频博主';
  if (/^\/api\/videos\/\d+\/(archive|restore)$/.test(path)) return '切换视频归档';
  if (/^\/api\/videos\/\d+$/.test(path)) return '修改视频';
  return `${record.requestMethod || ''} ${path}`.trim() || '其他操作';
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} 分 ${seconds % 60} 秒`;
}

async function loadCreators() {
  try {
    creators.value = await api.creators();
  } catch (error) {
    Message.error(error.message);
  }
}

async function setCreatorCollection(creator, enabled) {
  if (creatorSavingIds.has(creator.id)) return;
  creatorSavingIds.add(creator.id);
  try {
    const updated = await api.setCreatorCollection(creator.id, enabled);
    creators.value = creators.value.map((item) => item.id === creator.id ? updated : item);
    Message.success(enabled ? '已开启该博主视频采集' : '已暂停该博主视频采集');
  } catch (error) {
    Message.error(error.message);
  } finally {
    creatorSavingIds.delete(creator.id);
  }
}

function confirmDeleteCreator(creator) {
  Modal.confirm({
    title: `删除博主：${creator.displayName || creator.username || creator.id}`,
    content: `博主会从管理列表移除，并暂停其 ${creator.videoCount} 条关联视频的后续采集。视频及历史数据保留。重新添加该主页可恢复博主，但采集仍需手动开启。`,
    okText: '确认删除',
    okButtonProps: { status: 'danger' },
    onOk: async () => {
      try {
        await api.deleteCreator(creator.id);
        Message.success('博主已删除，关联视频已暂停采集');
        await loadCreators();
      } catch (error) {
        Message.error(error.message);
        throw error;
      }
    },
  });
}

async function loadProducts() {
  try {
    productData.value = await api.products();
  } catch (error) {
    if (page.value === 'products') Message.error(error.message);
  }
}

function openProductEditor(product = null) {
  productEditingId.value = product?.id ?? null;
  productName.value = product?.name ?? '';
  productVisible.value = true;
}

async function saveProduct() {
  if (!productName.value.trim()) return Message.warning('请输入商品名称');
  productSaving.value = true;
  try {
    if (productEditingId.value) await api.updateProduct(productEditingId.value, productName.value.trim());
    else await api.createProduct(productName.value.trim());
    Message.success(productEditingId.value ? '商品名称已更新' : '商品已添加');
    productVisible.value = false;
    productName.value = '';
    productEditingId.value = null;
    await loadProducts();
  } catch (error) {
    Message.error(error.message);
  } finally {
    productSaving.value = false;
  }
}

function viewProductVideos(product) {
  filters.productId = product.id;
  filters.page = 1;
  navigate('videos');
}

async function loadSettings() {
  settingsLoading.value = true;
  try {
    const [collectionSettings, feishuSettings] = await Promise.all([api.settings(), api.feishuSettings()]);
    Object.assign(settings, collectionSettings);
    Object.assign(feishu, feishuSettings, { appSecret: '' });
  } catch (error) {
    Message.error(error.message);
  } finally {
    settingsLoading.value = false;
  }
}

async function saveFeishuSettings() {
  if (!feishu.appId || !feishu.spreadsheetUrl || (!feishu.hasSecret && !feishu.appSecret)) {
    Message.warning('请填写 App ID、App Secret 和飞书表格链接');
    return;
  }
  feishuLoading.value = true;
  try {
    const body = { appId: feishu.appId.trim(), spreadsheetUrl: feishu.spreadsheetUrl.trim() };
    if (feishu.appSecret) body.appSecret = feishu.appSecret;
    Object.assign(feishu, await api.updateFeishuSettings(body), { appSecret: '' });
    Message.success('飞书同步配置已安全保存');
  } catch (error) {
    Message.error(error.message);
  } finally {
    feishuLoading.value = false;
  }
}

async function syncFeishuNow() {
  feishuSyncing.value = true;
  try {
    const result = await api.syncFeishu();
    Message.success(result.message);
    Object.assign(feishu, await api.feishuSettings(), { appSecret: '' });
  } catch (error) {
    Message.error(error.message);
    await loadSettings();
  } finally {
    feishuSyncing.value = false;
  }
}

async function saveSettings() {
  settingsLoading.value = true;
  try {
    Object.assign(settings, await api.updateSettings({
      frequencyType: settings.frequencyType,
      timeOfDay: settings.timeOfDay,
      weekday: Number(settings.weekday),
      monthday: Number(settings.monthday),
      intervalDays: Number(settings.intervalDays),
      isPaused: settings.isPaused,
      monthlyBudgetCny: Number(settings.monthlyBudgetCny),
    }));
    Message.success('统一采集设置已保存');
  } catch (error) {
    Message.error(error.message);
  } finally {
    settingsLoading.value = false;
  }
}

function navigate(target) {
  page.value = target;
}

watch(page, async (target) => {
  try {
    window.localStorage.setItem(PAGE_STORAGE_KEY, target);
  } catch {
    // 浏览器禁用本地存储时仍保持当前会话正常导航。
  }
  if (target === 'home') await Promise.all([loadDashboard(), loadStats()]);
  if (target === 'videos') await refreshVideoData();
  if (target === 'products') await loadProducts();
  if (target === 'tasks') await loadTasks();
  if (target === 'tasks') await loadSettings();
  if (target === 'logs') await loadLogs(1);
  if (target === 'creators') await loadCreators();
  if (target === 'settings') await loadSettings();
});

onMounted(async () => {
  window.addEventListener('pageshow', resetFeishuRedirect);
  clockTimer = window.setInterval(() => { currentTime.value = new Date(); }, 60_000);
  api.feishuConfig().then((result) => { feishuLoginEnabled.value = result.enabled; }).catch(() => {});
  try {
    currentUser.value = await api.session();
    await loadApplication();
  } catch (error) {
    if (error.status !== 401) Message.error(error.message);
  } finally {
    authLoading.value = false;
    showFeishuResult();
  }
});

onUnmounted(() => {
  window.removeEventListener('pageshow', resetFeishuRedirect);
  resetFeishuRedirect();
  window.clearInterval(pollTimer);
  window.clearInterval(clockTimer);
  window.clearTimeout(importPollTimer);
});
</script>

<template>
  <div v-if="authLoading" class="auth-loading-page">
    <a-spin :loading="true" tip="正在连接系统…" />
  </div>

  <section v-else-if="!currentUser" class="login-page">
    <div class="login-backdrop"><i></i><i></i><i></i></div>
    <div class="login-brand">
      <span class="brand-mark"><i></i><i></i><i></i><i></i></span>
      <div><b>欧易国际</b><small>视频数据系统</small></div>
    </div>
    <div class="login-card">
      <div class="login-card-heading"><span>欢迎回来</span><h1>登录工作台</h1><p>使用管理员账号继续管理视频数据</p></div>
      <a-form :model="loginForm" layout="vertical" @submit-success="login">
        <a-form-item label="用户名"><a-input v-model="loginForm.username" size="large" allow-clear placeholder="请输入用户名" autocomplete="username" /></a-form-item>
        <a-form-item label="密码"><a-input-password v-model="loginForm.password" size="large" placeholder="请输入密码" autocomplete="current-password" @press-enter="login" /></a-form-item>
        <a-alert v-if="loginError" type="error" :show-icon="true">{{ loginError }}</a-alert>
        <a-button type="primary" size="large" long html-type="submit" :loading="loginLoading">登录</a-button>
      </a-form>
      <a-button v-if="feishuLoginEnabled" size="large" long style="margin-top: 12px" :loading="feishuRedirecting === 'login'" :disabled="Boolean(feishuRedirecting)" @click="openFeishuLogin()">{{ feishuRedirecting === 'login' ? '正在打开飞书登录…' : '使用飞书登录' }}</a-button>
      <div class="login-security">安全登录 · 密码加密存储 · 12 小时会话</div>
    </div>
    <p class="login-footer">© 2026 欧易国际 · 数据仅供内部使用</p>
  </section>

  <div v-else class="app-shell">
    <aside class="sidebar">
      <div class="brand">
        <span class="brand-mark"><i></i><i></i><i></i><i></i></span>
        <span>欧易国际</span>
      </div>
      <div class="sidebar-label">WORKSPACE</div>
      <button
        v-for="item in navigation.slice(0, -1)"
        :key="item.key"
        class="nav-button"
        :class="{ active: page === item.key }"
        @click="navigate(item.key)"
      >
        <span class="nav-icon">{{ item.icon }}</span>
        <span>{{ item.label }}</span>
      </button>
      <div class="sidebar-label preferences">PREFERENCES</div>
      <button class="nav-button" :class="{ active: page === 'settings' }" @click="navigate('settings')">
        <span class="nav-icon">⚙</span><span>系统设置</span>
      </button>
      <div class="sidebar-bottom">
        <div class="motto"><b>让每一次变化都有记录</b><span>四个平台，一处查看。</span></div>
        <a-dropdown trigger="click" position="tr">
          <button class="user-row"><span class="user-avatar">{{ (currentUser.feishu?.name || currentUser.username).slice(0, 1).toUpperCase() }}</span><span>{{ currentUser.feishu?.name || currentUser.username }}<small>{{ currentUser.feishu ? '飞书成员 · 共享后台' : '系统账号' }} · 点击管理账户</small></span><b>⌄</b></button>
          <template #content>
            <a-doption @click="changePasswordVisible = true">修改密码</a-doption>
            <a-doption @click="logout">退出登录</a-doption>
          </template>
        </a-dropdown>
      </div>
    </aside>

    <main class="workspace">
      <header class="topbar">
        <div>工作台 <span>/</span> <b>{{ pageTitle }}</b></div>
        <div class="topbar-right"><span>北京时间 · {{ beijingTime }}</span><div class="environment"><i></i>{{ isLive ? '真实采集' : '开发模式' }} · MySQL</div></div>
      </header>

      <section v-if="page === 'home'" class="page-content dashboard-page">
        <div class="page-heading">
          <div><h1>数据总览</h1><p>查看四个平台的当前规模、采集状态与月度数据变化。</p></div>
          <div class="heading-actions"><a-button @click="loadDashboard">刷新统计</a-button><a-button type="primary" @click="navigate('videos')">进入视频管理</a-button></div>
        </div>

        <section class="panel date-report-panel">
          <div class="date-report-heading"><div><h2>时间段统计</h2><p>发布日期和采集时间分别统计，均按北京时间筛选。</p></div><div class="date-report-tabs"><button :class="{ active: reportMode === 'published' }" @click="changeReportMode('published')">按发布日期</button><button :class="{ active: reportMode === 'collection' }" @click="changeReportMode('collection')">按采集时间</button></div></div>
          <div class="date-report-controls"><label>开始日期<input v-model="reportFrom" type="date" /></label><label>结束日期<input v-model="reportTo" type="date" /></label><a-button type="primary" :loading="reportLoading" @click="loadReport">查询</a-button></div>
          <a-spin :loading="reportLoading" class="date-report-loading">
            <template v-if="reportData && reportMode === 'published'">
              <div class="date-report-values"><div><small>期间发布视频</small><strong>{{ reportData.videos }}</strong></div><div><small>这些视频的当前累计播放</small><strong>{{ compactNumber(reportData.views) }}</strong></div><div><small>发布日期未知</small><strong>{{ reportData.unknownPublishDates }}</strong></div></div>
              <div class="date-report-platforms"><span v-for="item in reportData.platforms" :key="item.platform"><PlatformLogo :platform="item.platform" :size="16" />{{ platformNames[item.platform] }} <b>{{ item.videos }} 条 · {{ compactNumber(item.views) }} 播放</b></span></div>
              <div class="date-report-foot"><small>播放量采用这些视频当前的累计值，不代表只在所选日期内产生的播放。</small><a-button size="small" @click="showPublishedVideos">查看对应视频</a-button></div>
            </template>
            <template v-else-if="reportData">
              <div class="date-report-values"><div><small>历史快照</small><strong>{{ reportData.snapshots }}</strong></div><div><small>采集到的视频</small><strong>{{ reportData.videos }}</strong></div><div><small>可比较的视频</small><strong>{{ reportData.comparableVideos }}</strong></div><div><small>首末快照播放变化</small><strong>{{ Number(reportData.viewChange) > 0 ? '+' : '' }}{{ compactNumber(reportData.viewChange) }}</strong></div></div>
              <div class="date-report-platforms"><span v-for="item in reportData.platforms" :key="item.platform"><PlatformLogo :platform="item.platform" :size="16" />{{ platformNames[item.platform] }} <b>{{ item.snapshots }} 次 · {{ item.comparableVideos }} 条可比较</b></span></div>
              <p class="date-report-note">播放变化只比较区间内同一视频至少两次有效快照；缺少基线或播放数据的记录不计入变化值。Instagram 更换采集来源前后的数据不直接比较。</p>
              <div class="date-report-history"><div v-for="point in [...reportData.points].reverse()" :key="point.date"><time>{{ point.date }}</time><span>{{ point.videos }} 条视频 · {{ point.snapshots }} 条快照</span><b>当日采集视频累计播放 {{ compactNumber(point.views) }}</b></div><div v-if="!reportData.points.length" class="muted">此时间段没有采集快照</div></div>
            </template>
          </a-spin>
        </section>

        <a-spin :loading="dashboardLoading" class="dashboard-loading">
          <h2 class="dashboard-overview-title">全站当前概览 <small>以下统计不受上方日期筛选影响</small></h2>
          <div class="stats-grid dashboard-stats">
            <article class="stat-card stat-card-detailed"><span class="stat-icon purple">▣</span><div><small>视频总数</small><strong>{{ stats.total }}</strong><div class="card-breakdown"><span v-for="item in platformList" :key="item.platform"><PlatformLogo :platform="item.platform" :size="13" />{{ item.videos }}</span></div></div></article>
            <article class="stat-card stat-card-detailed"><span class="stat-icon green">◈</span><div><small>有效播放合计</small><strong>{{ compactNumber(stats.totalViews) }}</strong><div class="card-breakdown"><span v-for="item in platformList" :key="item.platform"><PlatformLogo :platform="item.platform" :size="13" />{{ compactNumber(item.views) }}</span></div></div></article>
            <article class="stat-card"><span class="stat-icon blue">✓</span><div><small>已更新</small><strong>{{ stats.success }}</strong><em>{{ stats.partial }} 条部分成功 · 成功与待处理分开显示</em></div></article>
            <article class="stat-card"><span class="stat-icon orange">!</span><div><small>待处理</small><strong>{{ stats.pending + stats.failed }}</strong><em>{{ stats.pending }} 条待采集 · {{ stats.failed }} 条失败</em></div></article>
          </div>

          <section class="panel trend-panel">
            <div class="panel-heading">
              <div><h2>月度采集趋势</h2><p>每个月取每条视频最后一次真实快照汇总，历史月份没有快照时按 0 展示。</p></div>
              <div class="metric-tabs"><button v-for="(label, key) in trendMetricLabels" :key="key" :class="{ active: trendMetric === key }" @click="trendMetric = key">{{ label }}</button></div>
            </div>
            <div class="trend-legend"><span v-for="line in chartLines" :key="line.platform"><PlatformLogo :platform="line.platform" :size="14" />{{ platformNames[line.platform] }} <b>{{ compactNumber(line.latest) }}</b></span></div>
            <div class="chart-wrap">
              <svg class="trend-chart" viewBox="0 0 920 242" preserveAspectRatio="none" role="img" :aria-label="`${trendMetricLabels[trendMetric]}月度趋势图`">
                <g v-for="guide in chartGuides" :key="guide.y"><line x1="64" x2="884" :y1="guide.y" :y2="guide.y" /><text x="52" :y="guide.y + 4" text-anchor="end">{{ guide.label }}</text></g>
                <polyline v-for="line in chartLines" :key="line.platform" :points="line.points" :stroke="line.color" />
                <text v-for="(month, index) in dashboard.trend.months" :key="month" :x="64 + index * 820 / Math.max(1, dashboard.trend.months.length - 1)" y="232" text-anchor="middle">{{ month.slice(2).replace('-', '/') }}</text>
              </svg>
            </div>
          </section>

          <div class="insight-layout">
            <section class="panel platform-overview">
              <div class="panel-heading"><div><h2>平台数据拆分</h2><p>视频、播放、互动和评论四个维度的当前累计值。</p></div></div>
              <div class="platform-stat-grid">
                <article v-for="item in platformList" :key="item.platform">
                  <div class="platform-stat-title"><span class="platform-pill"><PlatformLogo :platform="item.platform" :size="23" />{{ platformNames[item.platform] }}</span><b>{{ item.videos }} 条视频</b></div>
                  <dl><div><dt>播放量</dt><dd>{{ compactNumber(item.views) }}</dd></div><div><dt>点赞/反应</dt><dd>{{ compactNumber(item.engagement) }}</dd></div><div><dt>评论数</dt><dd>{{ compactNumber(item.comments) }}</dd></div><div><dt>采集正常</dt><dd>{{ item.success }}/{{ item.videos }}</dd></div></dl>
                </article>
              </div>
            </section>
            <section class="panel more-stats">
              <div class="panel-heading"><div><h2>更多统计</h2><p>当前数据库中的真实汇总。</p></div></div>
              <div class="more-stat-list">
                <div><span>互动总量</span><b>{{ compactNumber(totalEngagement) }}</b><small>点赞与 Facebook 反应</small></div>
                <div><span>评论总量</span><b>{{ compactNumber(totalComments) }}</b><small>四平台公开评论数</small></div>
                <div><span>已识别作者</span><b>{{ dashboard.summary.creators }}</b><small>平台账号去重</small></div>
                <div><span>历史快照</span><b>{{ dashboard.summary.snapshots }}</b><small>长期趋势数据基础</small></div>
                <div><span>播放覆盖率</span><b>{{ dashboard.summary.coverageRate }}%</b><small>{{ dashboard.summary.covered }}/{{ dashboard.summary.videos }} 条有播放量</small></div>
                <div><span>分享 / 收藏 / 转发</span><b>{{ compactNumber(Number(dashboard.summary.shares) + Number(dashboard.summary.saves) + Number(dashboard.summary.reposts)) }}</b><small>平台返回字段合计</small></div>
              </div>
            </section>
          </div>

          <div class="schedule-banner dashboard-schedule">
            <span>◷</span><div><b>统一采集计划</b><small>最近采集：{{ formatDate(dashboard.summary.lastCollectedAt) }} · {{ isLive ? '当前使用真实数据采集，操作会产生接口费用。' : '当前处于开发模式。' }}</small></div>
            <a-button size="small" @click="refreshAll">立即全量采集</a-button>
          </div>
        </a-spin>
      </section>

      <section v-else-if="page === 'videos'" class="page-content video-management-page">
        <div class="page-heading">
          <div><h1>视频管理</h1><p>完整查看、筛选和维护所有视频；点击链接图标可直接打开原视频。</p></div>
          <div class="heading-actions">
            <a-button @click="exportVideos">⇩ 导出数据</a-button>
            <a-button @click="openImportDialog('file')">⇧ 批量上传</a-button>
            <a-button type="primary" @click="openImportDialog('paste')">＋ 录入视频</a-button>
          </div>
        </div>

        <div class="panel table-panel">
          <div class="video-tabs">
            <button :class="{ active: videoTab === 'all' }" @click="changeVideoTab('all')">全部视频 <b>{{ stats.total }}</b></button>
            <button :class="{ active: videoTab === 'failed' }" @click="changeVideoTab('failed')">采集失败 <b>{{ stats.failed }}</b></button>
            <button :class="{ active: videoTab === 'archived' }" @click="changeVideoTab('archived')">已归档</button>
          </div>
          <div class="filters">
            <a-input v-model="filters.search" allow-clear placeholder="搜索视频文案、作者或视频 ID" @press-enter="applyFilters" />
            <a-select v-model="filters.platform" @change="applyFilters">
              <a-option value="all">全部平台</a-option>
              <a-option v-for="(name, key) in platformNames" :key="key" :value="key">{{ name }}</a-option>
            </a-select>
            <a-select v-model="filters.productId" @change="applyFilters">
              <a-option value="all">全部商品</a-option>
              <a-option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</a-option>
            </a-select>
            <a-select v-model="filters.sort" @change="applyFilters">
              <a-option value="created_desc">最近录入</a-option>
              <a-option value="published_desc">发布时间</a-option>
              <a-option value="views_desc">播放量</a-option>
              <a-option value="success_desc">最后采集</a-option>
            </a-select>
            <a-button :disabled="!selectedKeys.length" @click="refreshSelected">刷新已选</a-button>
          </div>
          <div class="video-date-filters"><a-select v-model="filters.dateType" class="video-date-type" @change="applyFilters"><a-option value="published">发布日期</a-option><a-option value="collected">采集时间</a-option></a-select><input v-model="filters.dateFrom" type="date" aria-label="开始日期" /><span>至</span><input v-model="filters.dateTo" type="date" aria-label="结束日期" /><a-button size="small" @click="applyFilters">筛选</a-button><a-button size="small" @click="clearDateRange">清除</a-button><small>{{ filters.dateType === 'collected' ? '筛出区间内有采集快照的视频；表中播放量为最新值' : '筛出区间内发布的视频；表中播放量为最新值' }}</small></div>
          <a-table
            v-model:selected-keys="selectedKeys"
            row-key="id"
            :columns="videoColumns"
            :data="videos"
            :loading="loading"
            :pagination="false"
            :row-selection="{ type: 'checkbox', showCheckedAll: true }"
            :scroll="{ x: 1160 }"
          >
            <template #content="{ record }">
              <div class="video-content-cell">
                <button class="video-thumbnail" @click="openDetail(record)">
                  <img v-if="record.thumbnailUrl && !brokenThumbnails.has(String(record.id))" :src="`/api/videos/${record.id}/thumbnail`" :alt="videoDisplayTitle(record)" loading="lazy" @error="markThumbnailBroken(record.id)" />
                  <span v-else><PlatformLogo :platform="record.platform" :size="30" /></span>
                  <i>▶</i>
                </button>
                <div><button class="content-link" @click="openDetail(record)">{{ videoDisplayTitle(record) }}</button><small><PlatformLogo :platform="record.platform" :size="14" />{{ platformNames[record.platform] }} · {{ formatDateOnly(record.publishedAt) }}</small></div>
              </div>
            </template>
            <template #author="{ record }">
              <div v-if="record.author" class="author-cell">
                <div class="author-avatar"><img v-if="record.author.avatarUrl && !brokenAvatars.has(String(record.author.id))" :src="`/api/creators/${record.author.id}/avatar`" :alt="record.author.displayName" loading="lazy" @error="markAvatarBroken(record.author.id)" /><span v-else>{{ record.author.displayName?.slice(0, 1) || '?' }}</span></div>
                <div><a v-if="record.author.profileUrl" :href="record.author.profileUrl" target="_blank" rel="noopener noreferrer">{{ record.author.displayName || '账号信息待补充' }}</a><template v-else>{{ record.author.displayName || '账号信息待补充' }}</template><small>{{ record.author.username ? `@${record.author.username}` : '用户名待补充' }}</small></div>
              </div>
              <span v-else class="muted">账号信息待补充</span>
            </template>
            <template #product="{ record }"><a-tag v-if="record.product" color="purple">{{ record.product.name }}</a-tag><span v-else class="muted">未关联</span></template>
            <template #views="{ record }"><b>{{ formatNumber(record.metrics.views) }}</b></template>
            <template #engagement="{ record }"><b>{{ formatNumber(record.platform === 'facebook' ? record.metrics.reactions : record.metrics.likes) }}</b><small class="muted">{{ record.platform === 'facebook' ? '反应' : '点赞' }}</small></template>
            <template #comments="{ record }"><b>{{ formatNumber(record.metrics.comments) }}</b></template>
            <template #status="{ record }"><a-tag :color="statusMeta(record.status).color">● {{ statusMeta(record.status).label }}</a-tag><small class="status-time">{{ formatDate(record.lastSuccessAt) }}</small></template>
            <template #actions="{ record }">
              <div class="row-actions"><a :href="record.canonicalUrl" target="_blank" rel="noopener noreferrer" title="打开原视频">↗</a><a-dropdown><a-button size="mini" class="action-button">···</a-button><template #content><a-doption @click="openDetail(record)">查看详情</a-doption><a-doption v-if="!record.archived" @click="refreshOne(record.id)">{{ isLive ? '采集真实数据' : '模拟刷新' }}</a-doption><a-doption @click="confirmArchive(record)">{{ record.archived ? '恢复' : '归档' }}</a-doption></template></a-dropdown></div>
            </template>
            <template #empty><div class="empty-state"><b>暂无视频</b><span>录入视频链接后，数据将显示在这里。</span></div></template>
          </a-table>
          <div class="pagination-row"><span>共 {{ pagination.total }} 条</span><a-pagination :current="pagination.page" :page-size="pagination.pageSize" :total="pagination.total" @change="changePage" /></div>
        </div>
      </section>

      <section v-else-if="page === 'products'" class="page-content product-management-page">
        <div class="page-heading"><div><h1>商品管理</h1><p>商品只维护名称，视频和红人数据根据关联关系自动汇总。</p></div><a-button type="primary" @click="openProductEditor()">＋ 添加商品</a-button></div>
        <div class="product-summary-grid">
          <article><small>商品总数</small><b>{{ productData.summary.products }}</b><span>已建立的商品名称</span></article>
          <article><small>已关联视频</small><b>{{ productData.summary.assigned }}/{{ productData.summary.videos }}</b><span>{{ productData.summary.unassigned }} 条待补关联</span></article>
          <article><small>累计播放</small><b>{{ compactNumber(productTotals.views) }}</b><span>所有商品视频合计</span></article>
          <article><small>累计互动</small><b>{{ compactNumber(productTotals.engagement) }}</b><span>点赞/反应、评论、分享、收藏</span></article>
        </div>
        <div class="panel table-panel spaced">
          <a-table :columns="productColumns" :data="products" :pagination="false" row-key="id" :scroll="{ x: 1025 }" size="small">
            <template #name="{ record }"><b>{{ record.name }}</b><small class="muted">{{ record.videoCount }} 条视频</small></template>
            <template #creators="{ record }">{{ record.creatorCount }}</template>
            <template #platforms="{ record }"><div class="product-platforms"><span v-for="platform in Object.keys(platformNames)" :key="platform"><PlatformLogo :platform="platform" :size="16" />{{ record.platformCounts[platform] }}</span></div></template>
            <template #views="{ record }"><b>{{ compactNumber(record.totalViews) }}</b></template>
            <template #engagement="{ record }"><b>{{ compactNumber(record.totalEngagement) }}</b></template>
            <template #rate="{ record }">{{ record.engagementRate }}%</template>
            <template #status="{ record }"><a-tag :color="record.attention ? 'orange' : record.videoCount ? 'green' : 'gray'">{{ record.attention ? `${record.attention} 条待处理` : record.videoCount ? '采集正常' : '暂无视频' }}</a-tag></template>
            <template #collectedAt="{ record }">{{ formatDate(record.lastCollectedAt) }}</template>
            <template #actions="{ record }"><div class="product-actions"><a-button size="mini" @click="viewProductVideos(record)">查看视频</a-button><a-button size="mini" @click="openProductEditor(record)">改名</a-button></div></template>
            <template #empty><div class="empty-state"><b>暂无商品</b><span>先添加一个商品名称，再录入并关联视频。</span></div></template>
          </a-table>
        </div>
      </section>

      <section v-else-if="page === 'tasks'" class="page-content">
        <div class="page-heading"><div><h1>采集任务</h1><p>查看每一轮自动与手动采集的进度、执行时间和结果。</p></div><div class="schedule-status" :class="scheduleState.tone"><i></i><div><small>当前状态</small><b>{{ scheduleState.label }}</b><span>{{ scheduleState.detail }}</span></div></div></div>
        <div v-if="latestScheduledTask" class="last-scheduled-summary"><span>最近一次自动采集</span><b>#{{ latestScheduledTask.id }}</b><span>{{ formatDate(latestScheduledTask.finishedAt || latestScheduledTask.createdAt) }}</span><span>{{ latestScheduledTask.success }} 成功 · {{ latestScheduledTask.partial }} 部分 · {{ latestScheduledTask.failed }} 失败</span><a-tag :color="statusMeta(latestScheduledTask.status).color">{{ statusMeta(latestScheduledTask.status).label }}</a-tag></div>
        <div class="panel table-panel spaced">
          <a-table :columns="taskColumns" :data="tasks" :pagination="false" row-key="id">
            <template #createdAt="{ record }">{{ formatDate(record.createdAt) }}</template>
            <template #trigger="{ record }">{{ triggerLabel(record.triggerType) }}</template>
            <template #count="{ record }">{{ record.total }} 条</template>
            <template #execution="{ record }"><div class="execution-time"><span>开始 {{ formatDate(record.startedAt) }}</span><span>完成 {{ formatDate(record.finishedAt) }}</span></div></template>
            <template #result="{ record }"><span class="result-success">{{ record.success }} 成功</span> · <span>{{ record.partial }} 部分</span> · <span class="result-fail">{{ record.failed }} 失败</span></template>
            <template #status="{ record }"><a-tag :color="statusMeta(record.status).color">{{ statusMeta(record.status).label }}</a-tag></template>
            <template #actions="{ record }"><a-button size="mini" @click="openTask(record)">详情</a-button></template>
          </a-table>
        </div>
      </section>

      <section v-else-if="page === 'logs'" class="page-content activity-log-page">
        <div class="page-heading"><div><h1>操作日志</h1><p>记录录入、采集、其他后台操作及实际飞书成员。</p></div><a-button @click="loadLogs(logPagination.page)">刷新日志</a-button></div>
        <div class="log-summary-grid">
          <div><small>日志总数</small><b>{{ logPagination.total }}</b><span>录入、采集与后台操作</span></div>
          <div><small>最近录入</small><b>{{ formatDate(logs.find((item) => item.type === 'import')?.occurredAt) }}</b><span>确认保存链接的时间</span></div>
          <div><small>最近采集</small><b>{{ formatDate(logs.find((item) => item.type === 'collection')?.finishedAt) }}</b><span>任务实际完成时间</span></div>
        </div>
        <div class="panel table-panel spaced">
          <a-table :columns="logColumns" :data="logs" :pagination="false" row-key="id" :scroll="{ x: 1480 }">
            <template #type="{ record }"><span class="log-type" :class="record.type">{{ record.type === 'import' ? '链接录入' : record.type === 'collection' ? '数据采集' : '后台操作' }}</span></template>
            <template #occurredAt="{ record }">{{ formatDate(record.occurredAt) }}</template>
            <template #scope="{ record }">{{ record.type === 'import' ? '新增视频链接' : record.type === 'collection' ? triggerLabel(record.triggerType) : operationLabel(record) }}</template>
            <template #operator="{ record }"><div v-if="record.operator?.source === 'feishu'" class="execution-time"><span>{{ record.operator.name || '飞书成员' }}</span><span>{{ record.operator.openId }}</span></div><span v-else>{{ record.operator?.source === 'system' ? '系统定时计划' : record.operator?.source === 'password' ? `系统账号：${record.operator.username}` : '历史记录未采集' }}</span></template>
            <template #count="{ record }">{{ record.type === 'operation' ? '—' : `${record.total} 条` }}</template>
            <template #execution="{ record }">
              <div v-if="record.type === 'collection'" class="execution-time"><span>开始 {{ formatDate(record.startedAt) }}</span><span>完成 {{ formatDate(record.finishedAt) }} · {{ formatDuration(record.durationSeconds) }}</span></div>
              <span v-else-if="record.type === 'import'" class="muted-inline">录入于 {{ formatDate(record.occurredAt) }}</span>
              <span v-else class="muted-inline">请求于 {{ formatDate(record.occurredAt) }}</span>
            </template>
            <template #result="{ record }">
              <template v-if="record.type === 'import'"><span class="result-success">{{ record.added }} 新增</span> · {{ record.duplicate }} 重复 · <span class="result-fail">{{ record.invalid }} 无效</span></template>
              <template v-else-if="record.type === 'collection'"><span class="result-success">{{ record.success }} 成功</span> · {{ record.partial }} 部分 · <span class="result-fail">{{ record.failed }} 失败</span></template>
              <template v-else>HTTP {{ record.httpStatus || '—' }}</template>
            </template>
            <template #status="{ record }"><a-tag :color="record.type === 'import' ? 'green' : record.type === 'operation' ? (record.status === 'success' ? 'green' : 'red') : statusMeta(record.status).color">{{ record.type === 'import' ? '已记录' : record.type === 'operation' ? (record.status === 'success' ? '成功' : '失败') : statusMeta(record.status).label }}</a-tag></template>
            <template #actions="{ record }"><a-button v-if="record.type === 'collection'" size="mini" @click="openTask({ id: record.sourceId })">详情</a-button></template>
          </a-table>
          <div class="pagination-row"><span>共 {{ logPagination.total }} 条</span><a-pagination :current="logPagination.page" :page-size="logPagination.pageSize" :total="logPagination.total" @change="changeLogPage" /></div>
        </div>
      </section>

      <section v-else-if="page === 'creators'" class="page-content">
        <div class="page-heading"><div><h1>博主管理</h1><p>视频解析会自动关联同平台账号，也可以直接录入作者主页。</p></div><a-button type="primary" @click="creatorImportVisible = true">＋ 添加作者</a-button></div>
        <a-alert type="warning">优先按平台作者 ID 自动关联，其次匹配用户名和主页；不会仅根据同名跨平台合并。</a-alert>
        <div class="creator-grid">
          <article v-for="creator in creators" :key="creator.id" class="panel creator-card">
            <div class="creator-main"><div class="creator-avatar"><img v-if="creator.avatarUrl && !brokenAvatars.has(String(creator.id))" :src="`/api/creators/${creator.id}/avatar`" :alt="creator.displayName" @error="markAvatarBroken(creator.id)" /><span v-else>{{ creator.displayName?.slice(0, 1) || '?' }}</span></div><div><h3><a v-if="creator.profileUrl" :href="creator.profileUrl" target="_blank" rel="noopener noreferrer">{{ creator.displayName }}</a><template v-else>{{ creator.displayName }}</template></h3><p>{{ creator.username ? `@${creator.username}` : '用户名待补充' }}</p></div></div>
            <span class="platform-pill"><PlatformLogo :platform="creator.platform" :size="23" />{{ platformNames[creator.platform] }}</span>
            <div class="creator-products"><small>关联商品</small><div><a-tag v-for="product in creator.products" :key="product.id" color="purple">{{ product.name }}</a-tag><span v-if="!creator.products?.length">暂未关联</span></div></div>
            <div class="creator-stats"><div><small>关联视频</small><b>{{ creator.videoCount }}</b></div><div><small>播放合计</small><b>{{ compactNumber(creator.totalViews) }}</b></div></div>
            <div class="creator-actions"><div><b>采集关联视频</b><small>{{ creator.collectionEnabled ? '定时和手动采集均可执行' : '已暂停定时和手动采集' }}</small></div><a-switch :model-value="creator.collectionEnabled" :loading="creatorSavingIds.has(creator.id)" @change="(enabled) => setCreatorCollection(creator, enabled)" /><a-button size="small" status="danger" @click="confirmDeleteCreator(creator)">删除</a-button></div>
          </article>
          <div v-if="!creators.length" class="panel empty-state"><b>暂无博主</b><span>视频首次采集后显示作者账号。</span></div>
        </div>
      </section>

      <section v-else class="page-content">
        <div class="page-heading"><div><h1>系统设置</h1><p>四个平台所有未归档视频使用同一个采集周期。</p></div></div>
        <div class="settings-grid">
          <div class="panel settings-card">
            <div class="section-title"><span class="stat-icon purple">◷</span><div><h2>统一采集计划</h2><p>手动刷新不会改变定时节奏</p></div></div>
            <a-form :model="settings" layout="vertical" :disabled="settingsLoading" @submit-success="saveSettings">
              <a-form-item label="采集频率"><a-select v-model="settings.frequencyType"><a-option value="manual">仅手动</a-option><a-option value="daily">每天</a-option><a-option value="weekly">每周</a-option><a-option value="monthly">每月</a-option><a-option value="custom_days">自定义每 N 天</a-option></a-select></a-form-item>
              <a-form-item v-if="settings.frequencyType !== 'manual'" label="执行时间（北京时间）"><a-time-picker v-model="settings.timeOfDay" format="HH:mm" value-format="HH:mm" /></a-form-item>
              <a-form-item v-if="settings.frequencyType === 'weekly'" label="执行日"><a-select v-model="settings.weekday"><a-option v-for="(day, index) in ['周日','周一','周二','周三','周四','周五','周六']" :key="index" :value="index">{{ day }}</a-option></a-select></a-form-item>
              <a-form-item v-if="settings.frequencyType === 'monthly'" label="每月日期"><a-input-number v-model="settings.monthday" :min="1" :max="31" /><template #extra>当月不足该日时改为最后一天。</template></a-form-item>
              <a-form-item v-if="settings.frequencyType === 'custom_days'" label="间隔天数"><a-input-number v-model="settings.intervalDays" :min="1" :max="365" /></a-form-item>
              <a-form-item label="月度预算（人民币）"><a-input-number v-model="settings.monthlyBudgetCny" :min="0" /></a-form-item>
              <div class="toggle-row"><div><b>自动采集计划</b><small>暂停后仍可手动刷新</small></div><a-switch v-model="settings.isPaused" :checked-value="false" :unchecked-value="true" /></div>
              <a-button type="primary" html-type="submit" :loading="settingsLoading">保存设置</a-button>
            </a-form>
          </div>
          <div>
            <div class="panel settings-card">
              <div class="section-title"><span class="stat-icon green">✓</span><div><h2>历史数据</h2><p>成功与部分成功结果均生成快照</p></div></div>
              <div class="summary-line"><span>保留策略</span><b class="success-text">一直保留</b></div>
              <div class="summary-line"><span>缺失值</span><b>保存为 null</b></div>
              <div class="summary-line"><span>Facebook</span><b>反应数单独存储</b></div>
            </div>
            <div class="panel settings-card spaced">
              <h2>当前计划</h2>
              <div class="summary-line"><span>自动采集</span><b>{{ settings.isPaused ? '已暂停' : '已启用' }}</b></div>
              <div class="summary-line"><span>下次执行</span><b>{{ settings.nextRunAt ? formatDate(settings.nextRunAt) : '按需手动执行' }}</b></div>
              <div class="summary-line"><span>时区</span><b>Asia/Shanghai</b></div>
            </div>
            <div class="panel settings-card spaced">
              <div class="section-title"><span class="stat-icon purple">飞</span><div><h2>飞书表格同步</h2><p>密钥加密保存，接口和页面均不回显</p></div></div>
              <a-form :model="feishu" layout="vertical" :disabled="feishuLoading" @submit-success="saveFeishuSettings">
                <a-form-item label="App ID"><a-input v-model="feishu.appId" placeholder="cli_..." /></a-form-item>
                <a-form-item label="App Secret"><a-input-password v-model="feishu.appSecret" :placeholder="feishu.hasSecret ? '已安全保存；留空表示不修改' : '请输入 App Secret'" autocomplete="off" /></a-form-item>
                <a-form-item label="目标飞书表格链接"><a-input v-model="feishu.spreadsheetUrl" placeholder="https://...feishu.cn/wiki/..." /></a-form-item>
                <div class="summary-line"><span>配置状态</span><b :class="feishu.configured ? 'success-text' : ''">{{ feishu.configured ? '已配置' : '未配置' }}</b></div>
                <div class="summary-line"><span>最近同步</span><b>{{ feishu.lastSyncAt ? formatDate(feishu.lastSyncAt) : '尚未同步' }}</b></div>
                <div v-if="feishu.lastSyncMessage" class="integration-result" :class="feishu.lastSyncStatus">{{ feishu.lastSyncMessage }}</div>
                <div class="integration-actions"><a-button type="primary" html-type="submit" :loading="feishuLoading">保存飞书配置</a-button><a-button :disabled="!feishu.configured" :loading="feishuSyncing" @click="syncFeishuNow">立即同步测试</a-button></div>
              </a-form>
            </div>
          </div>
        </div>
      </section>
    </main>

    <a-modal v-model:visible="importVisible" :width="720" :footer="false" :title="importMode === 'file' ? '批量上传视频' : '录入视频'" :closable="!importCollectionActive" :mask-closable="!importCollectionActive" :esc-to-close="!importCollectionActive" @close="closeImportDialog">
      <template v-if="importStage === 'input'">
        <div v-if="importMode === 'file'" class="spreadsheet-import">
          <div class="spreadsheet-step"><b>1. 下载模板并填写</b><a :href="importTemplateUrl" download="视频批量导入模板.xlsx" class="template-download">下载 Excel 模板</a></div>
          <p class="modal-help">每行填写一条视频链接。商品名称可逐行填写，空白时使用默认商品；博主主页链接可选，填写后会绑定同平台博主，留空则采集时自动识别。单次最多 500 条。</p>
          <div class="import-product-field"><label>默认关联商品（可选）</label><a-select v-model="importProductId" allow-clear placeholder="表格未填写商品名称时使用"><a-option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</a-option></a-select><small v-if="!products.length">请先到“商品管理”添加商品名称。</small></div>
          <div class="spreadsheet-step"><b>2. 选择填写好的表格</b><label class="file-picker"><input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :disabled="importing" @change="selectImportFile" />{{ importFileName || '选择 .xlsx 文件' }}</label></div>
        </div>
        <template v-else>
          <div class="import-product-field"><label>关联商品</label><a-select v-model="importProductId" placeholder="请选择本次视频所属商品"><a-option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</a-option></a-select><small v-if="!products.length">请先到“商品管理”添加商品名称。</small></div>
          <p class="modal-help">每行一条 TikTok、Instagram、Facebook 或 YouTube 链接。链接将先在后端规范化和去重。</p>
          <a-textarea v-model="importLinks" :auto-size="{ minRows: 7, maxRows: 12 }" placeholder="https://www.youtube.com/watch?v=..." />
        </template>
        <div class="modal-actions"><a-button :loading="importing" @click="previewLinks">{{ importMode === 'file' ? '重新校验表格' : '校验链接' }}</a-button><a-button type="primary" :loading="importing" :disabled="!(importPreview?.summary.new || importPreview?.summary.duplicate) || (importMode === 'paste' && !importProductId)" @click="confirmLinks">{{ importMode === 'file' ? '一键导入并采集' : '采集数据' }}</a-button></div>
        <template v-if="importPreview">
          <div class="preview-summary"><b>新增 {{ importPreview.summary.new }}</b><span>重复 {{ importPreview.summary.duplicate }}</span><span>无效 {{ importPreview.summary.invalid }}</span><span>待解析 {{ importPreview.summary.pending }}</span></div>
          <div class="preview-list"><div v-for="row in importPreview.rows" :key="row.rowNumber" class="preview-row"><span>{{ importMode === 'file' ? `第 ${row.rowNumber} 行` : row.platformName || '未知平台' }}</span><code :title="row.message || row.originalUrl">{{ row.message || (row.creator?.state === 'valid' ? `${row.originalUrl} · 博主 @${row.creator.username}` : row.originalUrl) }}</code><a-tag :color="row.result === 'new' ? 'green' : row.result.startsWith('duplicate') ? 'gray' : 'orange'">{{ { new: '可录入', duplicate_input: '输入重复', duplicate_database: '已存在，可关联', invalid: '无效', pending_resolution: '待解析' }[row.result] }}</a-tag></div></div>
        </template>
      </template>
      <div v-else-if="importStage === 'saving'" class="import-saving"><a-spin /><b>正在校验并写入数据库，请稍候…</b><span>请勿重复提交。</span></div>
      <template v-else-if="importTask">
        <div class="collection-heading"><div><b>{{ importCollectionDone ? '采集已完成' : '正在采集数据' }}</b><span>共 {{ importTask?.total || importTask?.items?.length || 0 }} 条，每条视频独立显示进度和结果。</span></div><a-tag :color="statusMeta(importTask?.status).color">{{ statusMeta(importTask?.status).label }}</a-tag></div>
        <div v-if="importResult" class="import-save-summary">已入库 {{ importResult.summary.new }} 条 · 已有关联 {{ importResult.summary.linked }} 条 · 重复 {{ importResult.summary.duplicate }} 条 · 无效 {{ importResult.summary.invalid }} 条</div>
        <div class="collection-progress-list">
          <div v-for="(item, index) in importTask?.items" :key="item.id" class="collection-progress-item" :class="item.status">
            <div class="collection-progress-top"><span class="collection-index">{{ index + 1 }}</span><div><b>{{ item.title || `${platformNames[item.platform]} · ${item.platformVideoId}` }}</b><small>{{ platformNames[item.platform] }} · {{ collectionStatusText(item) }}</small></div><strong>{{ collectionProgress(item.status) }}%</strong></div>
            <div class="collection-progress-track"><i :style="{ width: `${collectionProgress(item.status)}%` }"></i></div>
          </div>
        </div>
        <div v-if="importCollectionDone" class="collection-result-summary"><span class="success-text">成功 {{ importTask.success }}</span><span>部分成功 {{ importTask.partial }}</span><span :class="{ 'error-text': importTask.failed }">失败 {{ importTask.failed }}</span></div>
        <div class="modal-actions"><a-button v-if="importCollectionDone" @click="resetImportDialog">继续录入</a-button><a-button v-if="importCollectionDone" type="primary" @click="closeImportDialog">关闭并查看数据</a-button><span v-else class="collection-wait-tip">采集期间请保持此窗口打开，完成后数据会自动刷新。</span></div>
      </template>
      <div v-else class="import-save-complete"><b>导入已完成</b><p>新增 {{ importResult?.summary.new || 0 }} 条，已有关联 {{ importResult?.summary.linked || 0 }} 条，重复 {{ importResult?.summary.duplicate || 0 }} 条，无效 {{ importResult?.summary.invalid || 0 }} 条。</p><div class="modal-actions"><a-button @click="resetImportDialog">继续录入</a-button><a-button type="primary" @click="closeImportDialog">关闭并查看数据</a-button></div></div>
    </a-modal>

    <a-modal v-model:visible="creatorImportVisible" :width="560" :footer="false" title="添加作者" @close="creatorProfileUrl = ''">
      <p class="modal-help">粘贴 TikTok、Instagram、Facebook 或 YouTube 作者主页。已有账号会直接复用，不会重复采集；新账号将获取公开资料并把头像保存到本服务器。</p>
      <a-input v-model="creatorProfileUrl" placeholder="https://www.instagram.com/username/" @press-enter="createCreator" />
      <div class="modal-actions"><a-button @click="creatorImportVisible = false">取消</a-button><a-button type="primary" :loading="creatorImporting" @click="createCreator">获取并添加</a-button></div>
    </a-modal>

    <a-drawer v-model:visible="detailVisible" :width="680" :footer="false" title="视频详情">
      <a-spin :loading="detailLoading" class="detail-loading">
        <template v-if="detail">
          <div class="detail-top"><span class="platform-pill"><PlatformLogo :platform="detail.platform" :size="23" />{{ platformNames[detail.platform] }}</span><a-tag :color="statusMeta(detail.status).color">{{ statusMeta(detail.status).label }}</a-tag></div>
          <h2 class="detail-title">{{ videoDisplayTitle(detail) }}</h2>
          <p class="detail-description">{{ detail.description || '暂无视频文案' }}</p>
          <a-alert v-if="detail.lastErrorMessage" type="error">{{ publicErrorMessage(detail.lastErrorMessage) }}</a-alert>
          <div class="creator-link-editor">
            <div><b>关联博主</b><small>{{ detail.creatorLinkMode === 'manual' ? '当前为人工关联，后续采集不会覆盖。' : '当前为自动关联，可人工改绑。' }}</small></div>
            <a-select v-model="creatorLinkDraft" allow-clear placeholder="未关联" style="width: 280px">
              <a-option v-for="creator in detailPlatformCreators" :key="creator.id" :value="creator.id">{{ creator.displayName }}{{ creator.username ? ` (@${creator.username})` : '' }}</a-option>
            </a-select>
            <a-button v-if="detail.creatorLinkMode === 'manual'" :loading="creatorLinkSaving" @click="saveCreatorLink('auto')">恢复自动</a-button>
            <a-button type="primary" :loading="creatorLinkSaving" @click="saveCreatorLink('manual')">保存人工关联</a-button>
          </div>
          <div class="product-link-editor"><div><b>关联商品</b><small>用于商品维度统计和后续飞书表格同步。</small></div><a-select v-model="productLinkDraft" allow-clear placeholder="未关联商品" style="width: 280px"><a-option v-for="product in products" :key="product.id" :value="product.id">{{ product.name }}</a-option></a-select><a-button type="primary" :loading="productLinkSaving" @click="saveProductLink">保存商品关联</a-button></div>
          <div class="detail-metrics">
            <div><small>播放量</small><b>{{ formatNumber(detail.metrics.views) }}</b></div>
            <div><small>{{ detail.platform === 'facebook' ? '反应数' : '点赞数' }}</small><b>{{ formatNumber(detail.platform === 'facebook' ? detail.metrics.reactions : detail.metrics.likes) }}</b></div>
            <div><small>评论数</small><b>{{ formatNumber(detail.metrics.comments) }}</b></div>
            <div><small>分享数</small><b>{{ formatNumber(detail.metrics.shares) }}</b></div>
            <div><small>收藏数</small><b>{{ formatNumber(detail.metrics.saves) }}</b></div>
            <div><small>转发数</small><b>{{ formatNumber(detail.metrics.reposts) }}</b></div>
          </div>
          <div class="detail-info">
            <div><span>视频 ID</span><b class="mono">{{ detail.platformVideoId }}</b></div>
            <div><span>发布时间</span><b>{{ formatDate(detail.publishedAt) }}</b></div>
            <div><span>最后尝试</span><b>{{ formatDate(detail.lastAttemptAt) }}</b></div>
            <div><span>最后成功</span><b>{{ formatDate(detail.lastSuccessAt) }}</b></div>
            <div><span>原视频</span><a :href="detail.canonicalUrl" target="_blank" rel="noopener noreferrer">打开链接 ↗</a></div>
          </div>
          <div class="section-row"><h3>播放趋势</h3><span class="section-meta">{{ detailViewTrend.points.length }} 个采集周期<span v-if="detailViewChange !== null"> · 较首期 {{ detailViewChange >= 0 ? '+' : '' }}{{ formatNumber(detailViewChange) }}</span></span></div>
          <p v-if="detailTrendSourceChanged" class="modal-help">采集来源已更换，趋势仅比较同一口径的快照；旧数据仍保留在下方历史记录中。</p>
          <div v-if="detailViewTrend.points.length >= 2" class="detail-trend-card">
            <div class="detail-trend-range"><b>{{ formatNumber(detailViewTrend.max) }}</b><span>播放量</span><b>{{ formatNumber(detailViewTrend.min) }}</b></div>
            <div class="detail-trend-plot">
              <svg :viewBox="`0 0 ${detailViewTrend.width} ${detailViewTrend.height}`" preserveAspectRatio="none">
                <line v-for="ratio in [0, .5, 1]" :key="ratio" x1="24" x2="576" :y1="24 + ratio * 132" :y2="24 + ratio * 132" />
                <polyline :points="detailViewTrend.polyline" />
                <circle v-for="point in detailViewTrend.points" :key="point.id" :cx="point.x" :cy="point.y" r="4"><title>{{ formatDate(point.collectedAt) }}：{{ formatNumber(point.views) }} 次播放</title></circle>
              </svg>
              <div class="detail-trend-labels"><span>{{ formatDate(detailViewTrend.points[0].collectedAt) }}</span><span>{{ formatDate(detailViewTrend.points.at(-1).collectedAt) }}</span></div>
            </div>
          </div>
          <div v-else class="detail-trend-empty"><b>{{ detailViewTrend.points.length ? '已记录首个采集周期' : '暂无播放趋势' }}</b><span>{{ detailViewTrend.points.length ? '再次采集后将形成播放趋势线。' : '成功采集后会按周期在这里生成趋势。' }}</span></div>
          <div class="section-row"><h3>历史快照</h3><a-button size="small" @click="refreshOne(detail.id)">{{ isLive ? '采集真实数据' : '模拟刷新' }}</a-button></div>
          <a-table :columns="snapshotColumns" :data="snapshots" :pagination="false" size="small" row-key="id">
            <template #time="{ record }">{{ formatDate(record.collectedAt) }}</template>
            <template #views="{ record }">{{ formatNumber(record.metrics.views) }}</template>
            <template #engagement="{ record }">{{ formatNumber(detail.platform === 'facebook' ? record.metrics.reactions : record.metrics.likes) }}</template>
            <template #comments="{ record }">{{ formatNumber(record.metrics.comments) }}</template>
            <template #status="{ record }"><a-tag :color="statusMeta(record.resultStatus).color">{{ statusMeta(record.resultStatus).label }}</a-tag></template>
          </a-table>
          <div class="note-editor"><label>备注</label><a-textarea v-model="noteDraft" :auto-size="{ minRows: 3, maxRows: 6 }" placeholder="添加关于这条视频的备注" /><a-button type="primary" @click="saveNote">保存备注</a-button></div>
        </template>
      </a-spin>
    </a-drawer>

    <a-modal v-model:visible="productVisible" :width="460" :footer="false" :title="productEditingId ? '修改商品名称' : '添加商品'" @close="productName = ''; productEditingId = null">
      <p class="modal-help">商品仅维护一个名称，统计数据会由关联视频自动汇总。</p>
      <a-input v-model="productName" :max-length="255" show-word-limit placeholder="例如：A01" @press-enter="saveProduct" />
      <div class="modal-actions"><a-button @click="productVisible = false">取消</a-button><a-button type="primary" :loading="productSaving" @click="saveProduct">保存</a-button></div>
    </a-modal>

    <a-modal v-model:visible="taskVisible" :footer="false" :width="780" title="任务详情">
      <template v-if="selectedTask">
        <div class="task-summary"><div><small>任务编号</small><b>#{{ selectedTask.id }}</b></div><div><small>处理数量</small><b>{{ selectedTask.total }}</b></div><div><small>结果</small><b>{{ selectedTask.success }} / {{ selectedTask.partial }} / {{ selectedTask.failed }}</b></div><div><small>状态</small><a-tag :color="statusMeta(selectedTask.status).color">{{ statusMeta(selectedTask.status).label }}</a-tag></div></div>
        <div class="task-items"><div v-for="item in selectedTask.items" :key="item.id"><span class="platform-pill"><PlatformLogo :platform="item.platform" :size="23" />{{ platformNames[item.platform] }}</span><div><b>{{ item.title || item.platformVideoId }}</b><small v-if="item.errorMessage">{{ publicErrorMessage(item.errorMessage) }}</small></div><a-tag :color="statusMeta(item.status).color">{{ statusMeta(item.status).label }}</a-tag></div></div>
      </template>
    </a-modal>

    <a-modal v-model:visible="changePasswordVisible" :footer="false" :width="460" title="修改密码" @close="Object.assign(changePasswordForm, { currentPassword: '', newPassword: '', confirmPassword: '' })">
      <p class="modal-help">修改成功后，其他设备上的旧登录状态会自动失效。</p>
      <a-form :model="changePasswordForm" layout="vertical" @submit-success="submitPasswordChange">
        <a-form-item label="当前密码"><a-input-password v-model="changePasswordForm.currentPassword" autocomplete="current-password" /></a-form-item>
        <a-form-item label="新密码"><a-input-password v-model="changePasswordForm.newPassword" autocomplete="new-password" /><template #extra>至少 10 位，包含大小写字母、数字、特殊字符中的至少三类。</template></a-form-item>
        <a-form-item label="确认新密码"><a-input-password v-model="changePasswordForm.confirmPassword" autocomplete="new-password" /></a-form-item>
        <div class="modal-actions"><a-button @click="changePasswordVisible = false">取消</a-button><a-button type="primary" html-type="submit" :loading="changePasswordLoading">确认修改</a-button></div>
      </a-form>
    </a-modal>
  </div>
</template>
